import { useState } from 'react';
import { toast } from 'sonner';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { BackendUser } from '@/infrastructure/clients/BackendClient';
import { OrgInstance, OrgMember } from '@/types/orgTypes';

type SavedField = 'full_name' | 'org_name' | 'email';
type Saved = Record<SavedField, string>;

const WRONG_PASSWORD = "Current password is incorrect. If you sign in with Google or Apple, email yourself a link to set one.";

interface ProfileFormSource {
  user: BackendUser | null;
  orgMember: OrgMember | null;
  orgInstance: OrgInstance | null;
  membershipKnown: boolean;
  refreshUser: () => Promise<void>;
  updateMember: (change: (member: OrgMember) => OrgMember) => void;
  updateInstance: (change: (instance: OrgInstance) => OrgInstance) => void;
}

const listOf = (items: string[]) => items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items.at(-1)}` : items[0];

export function useProfileForm({ user, orgMember, orgInstance, membershipKnown, refreshUser, updateMember, updateInstance }: ProfileFormSource) {
  const services = useServices();
  const source: Saved = {
    full_name: orgMember?.fullName ?? '',
    org_name: orgInstance?.orgInstanceDisplayName ?? '',
    email: user?.email ?? '',
  };

  const [original, setOriginal] = useState<Saved>(source);
  const [formData, setFormData] = useState({ ...source, new_email: '', current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);

  // A saved value changed (a load, a save, or an edit elsewhere): fields the user has not touched take it on
  const changed = (Object.keys(source) as SavedField[]).filter(key => source[key] !== original[key]);
  if (changed.length > 0) {
    setOriginal(source);
    setFormData(prev => {
      const next = { ...prev };
      for (const key of changed) if (prev[key] === original[key]) next[key] = source[key];
      return next;
    });
  }

  const isOwner = orgMember?.memberType === 'owner';
  const fullNameChanged = membershipKnown && formData.full_name.trim() !== '' && formData.full_name.trim() !== original.full_name;
  const orgNameChanged = membershipKnown && isOwner && formData.org_name.trim() !== '' && formData.org_name.trim() !== original.org_name;
  const emailChanged = formData.new_email.trim() !== '' && formData.new_email !== formData.email;
  const hasProfileChanges = fullNameChanged || orgNameChanged || emailChanged;
  const canSaveProfile = !saving && hasProfileChanges && (!emailChanged || formData.current_password !== '');

  const verifyPassword = async () => {
    const { error } = await services.supabase.auth.signInWithPassword({ email: formData.email, password: formData.current_password });
    if (error) toast.error(WRONG_PASSWORD);
    return !error;
  };

  const updateProfile = async () => {
    if (!user?.id || !canSaveProfile) return;
    setSaving(true);
    const saved: string[] = [];
    const failed: string[] = [];
    try {
      if (emailChanged && !(await verifyPassword())) return;

      if (fullNameChanged) {
        const typed = formData.full_name;
        const fullName = typed.trim();
        try {
          await services.organization.updateOrgMemberFullName(user.id, fullName);
          updateMember(member => ({ ...member, fullName }));
          setFormData(prev => (prev.full_name === typed ? { ...prev, full_name: fullName } : prev));
          saved.push('name');
        } catch (error) {
          console.error('Error updating full name:', error);
          failed.push('name');
        }
      }

      if (orgNameChanged) {
        const typed = formData.org_name;
        const displayName = typed.trim();
        try {
          await services.organization.updateOrgInstanceDisplayName(user.id, displayName);
          updateInstance(instance => ({ ...instance, orgInstanceDisplayName: displayName }));
          setFormData(prev => (prev.org_name === typed ? { ...prev, org_name: displayName } : prev));
          saved.push('organization name');
        } catch (error) {
          console.error('Error updating organization name:', error);
          failed.push('organization name');
        }
      }

      let emailStarted = false;
      if (emailChanged) {
        const { error } = await services.supabase.auth.updateUser({ email: formData.new_email.trim() });
        if (error) {
          console.error('Error updating email:', error);
          failed.push('email');
        } else {
          emailStarted = true;
          setFormData(prev => ({ ...prev, new_email: '', current_password: '' }));
          await refreshUser();
        }
      }

      if (failed.length > 0) {
        toast.error(saved.length > 0
          ? `Saved your ${listOf(saved)}, but couldn't save your ${listOf(failed)}. Please try again.`
          : `Couldn't save your ${listOf(failed)}. Please try again.`);
      } else if (emailStarted) {
        toast.success('Email update started. Check your new email to confirm it.');
      } else {
        toast.success('Profile updated');
      }
    } finally {
      setSaving(false);
    }
  };

  const updatePassword = async () => {
    if (saving) return;
    if (!formData.current_password) {
      toast.error('Current password is required');
      return;
    }
    if (formData.new_password !== formData.confirm_password) {
      toast.error("New passwords don't match");
      return;
    }
    if (formData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (formData.current_password === formData.new_password) {
      toast.error('New password must be different from current password');
      return;
    }

    setSaving(true);
    try {
      if (!(await verifyPassword())) return;
      const { error } = await services.supabase.auth.updateUser({ password: formData.new_password });
      if (error) {
        toast.error(error.message || 'Failed to update password');
        return;
      }
      toast.success('Password updated');
      setFormData(prev => ({ ...prev, current_password: '', new_password: '', confirm_password: '' }));
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const [resetSent, setResetSent] = useState(false);
  const sendPasswordLink = async () => {
    if (!formData.email || resetSent) return;
    const { error } = await services.supabase.auth.resetPasswordForEmail(formData.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      toast.error("Couldn't send the email. Please try again.");
      return;
    }
    setResetSent(true);
    toast.success(`We sent a link to ${formData.email} to set your password.`);
  };

  return {
    formData,
    setFormData,
    saving,
    isOwner,
    emailChanged,
    canSaveProfile,
    updateProfile,
    updatePassword,
    sendPasswordLink,
    resetSent,
  };
}
